/**
 * AnnotationCanvas
 *
 * Transparent full-area `<canvas>` overlay used during pen/annotation
 * mode in `PresentationMode`.  The presenter drags to draw freehand
 * red strokes directly on top of the current slide.
 *
 * Stroke storage:
 *   Points are stored in *normalized* canvas-relative coordinates
 *   (0..1 on both axes).  This lets the parent (PresentationMode)
 *   hand the same stroke list back on a resize / window re-layout and
 *   the strokes still line up with the same spot on the slide image.
 *
 * Stroke lifecycle:
 *   - pointerdown  → begin a new stroke, store the starting point
 *   - pointermove  → append points to the in-progress stroke and
 *     redraw (current strokes + the in-progress stroke segment)
 *   - pointerup / pointercancel → commit the finished stroke via
 *     `onAddStroke` and clear the in-progress state
 *
 * The component is intentionally dumb about *which* slide these
 * strokes belong to — `slideId` is only used as a signal to reset the
 * in-progress drag buffer when the current slide changes.  The parent
 * owns the `Map<slideId, Stroke[]>` store and passes the right slice
 * in via `strokes`.
 *
 * Canvas sizing:
 *   A `ResizeObserver` keeps the canvas's internal pixel buffer in
 *   sync with its CSS box.  On every resize we redraw all strokes.
 */
import { useCallback, useEffect, useRef, useState } from "react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StrokePoint {
  /** Normalized 0..1 X coordinate relative to the canvas box. */
  x: number
  /** Normalized 0..1 Y coordinate relative to the canvas box. */
  y: number
}

export interface Stroke {
  points: StrokePoint[]
  color: string
  width: number
}

export interface AnnotationCanvasProps {
  active: boolean
  /**
   * Current slide id — the parent uses this key to swap stroke lists
   * when slides change; the component itself uses it only as a signal
   * to reset any in-progress drag state.
   */
  slideId: string
  strokes: Stroke[]
  onAddStroke: (stroke: Stroke) => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** v1: single fixed stroke color / width (spec). */
const STROKE_COLOR = "rgb(255, 0, 0)"
const STROKE_WIDTH = 3

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface DrawContext {
  ctx: CanvasRenderingContext2D
  width: number
  height: number
}

function drawStroke(dc: DrawContext, stroke: Stroke): void {
  if (stroke.points.length === 0) return
  const { ctx, width: w, height: h } = dc
  ctx.strokeStyle = stroke.color
  ctx.lineWidth = stroke.width
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.beginPath()
  const first = stroke.points[0]
  ctx.moveTo(first.x * w, first.y * h)
  if (stroke.points.length === 1) {
    // Single-point "stroke" — render a small dot so a simple click still
    // leaves a visible mark.
    ctx.lineTo(first.x * w + 0.01, first.y * h + 0.01)
  } else {
    for (let i = 1; i < stroke.points.length; i++) {
      const p = stroke.points[i]
      ctx.lineTo(p.x * w, p.y * h)
    }
  }
  ctx.stroke()
}

function redraw(canvas: HTMLCanvasElement, strokes: Stroke[], inProgress: Stroke | null): void {
  const ctx = canvas.getContext("2d")
  if (!ctx) return
  const { width, height } = canvas
  const dc: DrawContext = { ctx, height, width }
  ctx.clearRect(0, 0, width, height)
  for (const s of strokes) {
    drawStroke(dc, s)
  }
  if (inProgress) {
    drawStroke(dc, inProgress)
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AnnotationCanvas({ active, slideId, strokes, onAddStroke }: AnnotationCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  // In-progress stroke lives in a ref for mutation during pointermove;
  // we keep a setState tick so redraws happen when we need them.
  const currentStrokeRef = useRef<Stroke | null>(null)
  const [, setRedrawTick] = useState(0)

  // ---- Sync canvas pixel size to its CSS box -----------------------------

  const syncSizeAndRedraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1
    const pxW = Math.max(1, Math.round(rect.width * dpr))
    const pxH = Math.max(1, Math.round(rect.height * dpr))
    if (canvas.width !== pxW) canvas.width = pxW
    if (canvas.height !== pxH) canvas.height = pxH
    redraw(canvas, strokes, currentStrokeRef.current)
  }, [strokes])

  useEffect(() => {
    syncSizeAndRedraw()
  }, [syncSizeAndRedraw])

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return
    const canvas = canvasRef.current
    if (!canvas) return
    const ro = new ResizeObserver(() => syncSizeAndRedraw())
    ro.observe(canvas)
    return () => ro.disconnect()
  }, [syncSizeAndRedraw])

  // ---- Reset in-progress drag when slide changes ------------------------

  useEffect(() => {
    currentStrokeRef.current = null
  }, [slideId])

  // ---- Pointer handlers --------------------------------------------------

  const toNormalized = useCallback((clientX: number, clientY: number): StrokePoint | null => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return null
    return {
      x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
    }
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!active) return
      // Only left-button / primary pen input.
      if (e.button !== 0 && e.pointerType === "mouse") return
      e.stopPropagation()
      e.preventDefault()
      const pt = toNormalized(e.clientX, e.clientY)
      if (!pt) return
      currentStrokeRef.current = {
        color: STROKE_COLOR,
        points: [pt],
        width: STROKE_WIDTH,
      }
      try {
        e.currentTarget.setPointerCapture(e.pointerId)
      } catch {
        // setPointerCapture can throw in jsdom/older browsers — safe to ignore.
      }
      setRedrawTick((t) => t + 1)
    },
    [active, toNormalized]
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!active) return
      const current = currentStrokeRef.current
      if (!current) return
      const pt = toNormalized(e.clientX, e.clientY)
      if (!pt) return
      current.points.push(pt)
      // Redraw by bumping the tick — strokes prop hasn't changed.
      const canvas = canvasRef.current
      if (canvas) redraw(canvas, strokes, current)
    },
    [active, strokes, toNormalized]
  )

  const finishStroke = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!active) return
      const current = currentStrokeRef.current
      if (!current) return
      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch {
        // Ignored — see setPointerCapture above.
      }
      currentStrokeRef.current = null
      onAddStroke(current)
    },
    [active, onAddStroke]
  )

  // ---- Render ------------------------------------------------------------

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="presentation-annotation-canvas"
      style={{
        cursor: active ? "crosshair" : "default",
        height: "100%",
        inset: 0,
        pointerEvents: active ? "auto" : "none",
        position: "absolute",
        touchAction: active ? "none" : "auto",
        width: "100%",
        zIndex: 50,
      }}
      onPointerCancel={finishStroke}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishStroke}
    />
  )
}
